import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../../contexts/LanguageContext';

const SEO = ({
  title,
  description,
  keywords,
  image = 'https://petcarenote.com/android-chrome-512x512.png',
  type = 'website',
  structuredData = null,
  canonical = null,
  noindex = false,
  article = null
}) => {
  const { language } = useLanguage();
  const baseUrl = 'https://petcarenote.com';

  // ✅ 取得當前 hash 路徑，確保語言前綴正確
  const getCurrentPath = () => {
    const hash = window.location.hash.replace('#', '') || '/';
    if (!hash.startsWith(`/${language}`)) {
      return `/${language}${hash.startsWith('/') ? hash : `/${hash}`}`;
    }
    return hash;
  };

  const currentPath = getCurrentPath();
  const fullUrl = canonical || `${baseUrl}/#${currentPath}`;

  const languageMapping = {
    en: 'en_US',
    zh: 'zh_TW',
    ja: 'ja_JP'
  };
  const ogLocale = languageMapping[language] || 'en_US';
  const alternateLocales = Object.values(languageMapping).filter(loc => loc !== ogLocale);

  // ✅ FIX: 修正路徑處理，避免 /en/ 結尾多餘斜線
  const getAlternateUrls = () => {
    // 移除開頭的 /，再移除語言前綴
    const pathWithoutLeadingSlash = currentPath.replace(/^\//, '');
    const pathWithoutLang = pathWithoutLeadingSlash
      .replace(/^(en|zh|ja)\//, '')  // 移除 en/ zh/ ja/ 前綴
      .replace(/^(en|zh|ja)$/, '');  // 移除純語言路徑（如 "en"）

    // 如果剩餘路徑為空，不加斜線；否則加 /
    const suffix = pathWithoutLang ? `/${pathWithoutLang}` : '';

    return {
      en: `${baseUrl}/#/en${suffix}`,
      zh: `${baseUrl}/#/zh${suffix}`,
      ja: `${baseUrl}/#/ja${suffix}`,
      'x-default': `${baseUrl}/#/en${suffix}`
    };
  };

  const alternateUrls = getAlternateUrls();

  const formatDateToISO8601 = (dateString) => {
    if (!dateString) return new Date().toISOString();
    if (dateString.includes('T')) return dateString;
    const date = new Date(dateString);
    return date.toISOString();
  };

  const getArticleStructuredData = () => {
    if (!article) return null;
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": title,
      "description": description,
      "image": {
        "@type": "ImageObject",
        "url": image,
        "width": 1200,
        "height": 630
      },
      "author": {
        "@type": "Organization",
        "name": article.author || "Pet Care Note Team",
        "url": baseUrl
      },
      "publisher": {
        "@type": "Organization",
        "name": "Pet Care Note",
        "logo": {
          "@type": "ImageObject",
          "url": "https://petcarenote.com/android-chrome-512x512.png",
          "width": 512,
          "height": 512
        }
      },
      "datePublished": formatDateToISO8601(article.publishDate),
      "dateModified": formatDateToISO8601(article.lastModified || article.publishDate),
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": fullUrl
      },
      "keywords": keywords,
      "articleSection": article.category,
      "inLanguage": language,
      "isAccessibleForFree": true
    };
  };

  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Pet Care Note",
    "description": "Comprehensive pet health management system",
    "url": baseUrl,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${baseUrl}/#/${language}/blog?search={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Pet Care Note",
      "logo": {
        "@type": "ImageObject",
        "url": "https://petcarenote.com/android-chrome-512x512.png",
        "width": 512,
        "height": 512
      }
    }
  };

  const finalStructuredData = structuredData || getArticleStructuredData() || defaultStructuredData;

  return (
    <Helmet>
      <html lang={language} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Robots: noindex 用於私人頁面 */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <>
          <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
          <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
          <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        </>
      )}

      {/* Canonical */}
      <link rel="canonical" href={fullUrl} />

      {/* ✅ Hreflang（含 self-reference 和 x-default） */}
      {Object.entries(alternateUrls).map(([lang, url]) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:locale" content={ogLocale} />
      {alternateLocales.map(locale => (
        <meta key={locale} property="og:locale:alternate" content={locale} />
      ))}
      <meta property="og:site_name" content="Pet Care Note" />

      {/* Article Open Graph */}
      {article && (
        <>
          <meta property="article:published_time" content={formatDateToISO8601(article.publishDate)} />
          <meta property="article:modified_time" content={formatDateToISO8601(article.lastModified || article.publishDate)} />
          <meta property="article:author" content={article.author || "Pet Care Note Team"} />
          <meta property="article:section" content={article.category} />
          {article.tags && article.tags.map(tag => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={title} />
      <meta name="twitter:site" content="@PetCareNote" />

      {/* Additional */}
      <meta name="author" content="Pet Care Note Team" />
      <meta name="publisher" content="Pet Care Note" />
      <meta name="theme-color" content="#3B82F6" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Pet Care Note" />

      {article && article.readTime && (
        <meta name="article:reading_time" content={`${article.readTime} minutes`} />
      )}

      {/* Structured Data */}
      {finalStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(finalStructuredData)}
        </script>
      )}

      {/* Breadcrumb for blog articles */}
      {article && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": `${baseUrl}/#/${language}` },
              { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${baseUrl}/#/${language}/blog` },
              { "@type": "ListItem", "position": 3, "name": title, "item": fullUrl }
            ]
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;